import type { ReactNode } from 'react';

import { Icon } from '@/lib/Icon';
import { cn } from '@/lib/cn';

/**
 * PageHeader — sunken greeting band used at the top of most patient
 * screens. Mono-caps headline + optional arrow-prefixed description
 * paragraph.
 *
 * The arrow-indent pattern mirrors system-web's product-description block
 * (`components/landers/product.js` etc.): the description wrapper is
 * `relative` with `indent-15`, and the arrow sits absolutely-positioned
 * at `-left-15 -top-2` so it hangs to the left of the first line. The
 * parent (`px-15`) supplies enough left padding for the arrow's negative
 * offset to stay inside the band.
 *
 * Usage:
 *   <PageHeader
 *     title={<>Hi {firstName},<br/>here is today's plan.</>}
 *     description="You have two administrations remaining…"
 *   />
 */
export interface PageHeaderProps {
  /** Optional mono-caps line above the headline (e.g. "Intake 2 of 3"). */
  eyebrow?: ReactNode;
  title: ReactNode;
  description?: ReactNode;
  /** Optional element rendered below the description, inside the band
   *  (e.g. the Home screen's "Complete Tasks" jump button). */
  action?: ReactNode;
  className?: string;
}

export function PageHeader({
  eyebrow,
  title,
  description,
  action,
  className,
}: PageHeaderProps) {
  return (
    <header
      className={cn(
        'bg-[var(--surface-sunken)] px-15 md:px-40 pt-25 pb-15',
        className,
      )}
    >
      <div className=" max-w-[58rem] mx-auto">
        {eyebrow && (
          <p className="type-label-sm text-ink-muted mb-10">{eyebrow}</p>
        )}
        <h1 className="type-headline-lg text-black">{title}</h1>
        {description && (
          <div className="relative indent-15 mt-5 font-body text-15 md:text-15 text-[#7C7C7C]">
            <div
              aria-hidden
              className="absolute top-4 left-0 text-[#A9A9A9]"
            >
              <Icon name="Arrow" viewBox="0 0 10 12" className="w-[.8rem]" />
            </div>
            {description}
          </div>
        )}
        {action && <div className="mt-15">{action}</div>}
      </div>
    </header>
  );
}
